/* eslint-disable react/no-multi-comp */

// Modules
import React from 'react';
import PropTypes from 'prop-types';
import Textarea from 'react-textarea-autosize';
import _ from 'lodash';
import classNames from 'classnames';
import moment from 'moment';

import { RelativeTime } from 'components/widgets/dates';
import { ignoreEnter } from 'utils/event-handlers';
import { isMobile } from 'constants';
import { prepEagerUpdate } from 'utils/eager-update-utils';
import SVG from 'components/widgets/icons/svg';
import UserInitials from 'components/models/user/user-initials';
import createUuid from 'uuid/v4';
import replaceById from 'utils/replace-by-id';

export default class Checklist extends React.Component {
  static propTypes = {
    updateTask: PropTypes.func.isRequired,
    task: PropTypes.object.isRequired,
    currentUser: PropTypes.object.isRequired,
    parent: PropTypes.object,
    isSublist: PropTypes.bool,
    addNewSubList: PropTypes.bool,
    removeNewSubList: PropTypes.func
  };

  constructor() {
    super();

    this.state = {};

    this.updateItem = this.updateItem.bind(this);
    this.injectItems = this.injectItems.bind(this);
    this.updateChecklist = this.updateChecklist.bind(this);
    this.completeUpdate = this.completeUpdate.bind(this);
    this.updateOrder = this.updateOrder.bind(this);
    this.getItems = this.getItems.bind(this);
    this.addItemInline = this.addItemInline.bind(this);
  }

  getItems() {
    const { task, parent, isSublist } = this.props;

    return _(task.checklist)
      .reject(item => isSublist && !item.parent)
      .filter({ parent: _.get(parent, 'id', null) })
      .reject({ status: 'Archived' })
      .sortBy([item => item.order || 0])
      .value();
  }

  updateOrder(items) {
    return _(items)
      .reject({ status: 'Archived' })
      .map((item, index) => {
        if (!_.isNil(item.order) && item.order === index) return null;

        return _.defaults({ order: index }, item);
      })
      .compact()
      .value();
  }

  updateItem(item) {
    this.updateChecklist([item]);
  }

  injectItems(items, index) {
    this.updateChecklist(items, index);
  }

  addItemInline(index) {
    this.setState({ addItemInline: index });
  }

  updateChecklist(items, index) {
    const { task, updateTask } = this.props;
    const checklist = replaceById(items, this.getItems(), { injectAt: index });
    const orderUpdate = this.updateOrder(checklist);
    const serverUpdate = replaceById(orderUpdate, items);
    const localUpdate = replaceById(_.map(serverUpdate, prepEagerUpdate), task.checklist);

    updateTask({ checklist: serverUpdate }, { checklist: localUpdate })
      .tap(savedTicket => this.completeUpdate(savedTicket, serverUpdate));
  }

  completeUpdate(savedTicket, serverUpdate) {
    const { task, updateTask } = this.props;
    const needsId = _.reject(serverUpdate, 'id');
    const savedTask = _.find(savedTicket.tasks, { id: task.id });
    const updatedItems = _.intersectionBy(savedTask.checklist, needsId, 'uuid');
    const ids = _.map(updatedItems, item => _.pick(item, ['id', 'uuid', 'updatedAt']));
    const updatedChecklist = replaceById(ids, task.checklist);

    if (needsId.length) updateTask(false, { checklist: updatedChecklist });
  }

  render() {
    const { task, parent } = this.props;
    const { addItemInline } = this.state;
    const complete = task.status === 'Complete';
    const items = this.getItems();

    if (addItemInline) items.splice(addItemInline, 0, { newItemInline: true });

    return (
      <div className="task-checklist">
        <ul className={classNames({ 'sub-list': parent })}>
          {_.map(items, (item, index) => {
            if (item.newItemInline) {
              return (
                <NewChecklistItem
                  {...this.props}
                  key={`new-item-inline-${_.get(parent, 'id', 0)}`}
                  index={index}
                  injectItems={this.injectItems}
                  addItemInline={this.addItemInline}
                />
              );
            }

            return (
              <ChecklistItem
                {...this.props}
                key={item.uuid}
                item={item}
                index={index}
                updateItem={this.updateItem}
                addItemInline={this.addItemInline}
              />
            );
          })}

          { !complete &&
            <NewChecklistItem
              {...this.props}
              key={`new-item-${_.get(parent, 'id', 0)}`}
              injectItems={this.injectItems}
              index={null}
              addItemInline={false}
            />
          }
        </ul>
      </div>
    );
  }
}

class NewChecklistItem extends React.Component {
  static propTypes = {
    task: PropTypes.object.isRequired,
    index: PropTypes.number,
    parent: PropTypes.object,
    addNewSubList: PropTypes.bool,
    removeNewSubList: PropTypes.func,
    addItemInline: PropTypes.any,
    injectItems: PropTypes.func
  };

  constructor() {
    super();

    this.state = {};

    this.addNewChecklistItem = this.addNewChecklistItem.bind(this);
    this.handleEnterKey = this.handleEnterKey.bind(this);
    this.pasteList = this.pasteList.bind(this);
  }

  addNewChecklistItem() {
    const { addNewSubList, removeNewSubList, index, parent } = this.props;
    const { newChecklistItem } = this.state;

    if (newChecklistItem) {
      const item = { status: 'Active', uuid: createUuid(), description: newChecklistItem, parent: _.get(parent, 'id', null) };

      this.props.injectItems([item], index);
      this.setState({ newChecklistItem: '' });

      if (addNewSubList) removeNewSubList();
      if (this.props.addItemInline) this.props.addItemInline(index + 1);
    } else {
      if (this.props.addItemInline) this.props.addItemInline();
    }
  }

  handleEnterKey(event) {
    event.target.blur();
    event.target.focus();
  }

  pasteList(event) {
    event.preventDefault();

    const { parent, index } = this.props;
    const value = event.clipboardData.getData('Text');

    if (value) {
      const pastedArray = _.split(value, '\n');
      const defaults = { status: 'Active', parent: _.get(parent, 'id', null) };
      const items = _.map(pastedArray, description => _.defaults({ uuid: createUuid(), description }, defaults));

      this.props.injectItems(items, index);
      this.setState({ newChecklistItem: '' });
      if (this.props.addItemInline) this.props.addItemInline(index + items.length);
    }
  }

  render() {
    const { addNewSubList, addItemInline } = this.props;
    const { newChecklistItem } = this.state;

    return (
      <li className="checklist-item">
        <div className="checklist-item-wrapper new-item" >
          <SVG className="task-checkbox" src="svg/check.svg" />
          <Textarea
            placeholder="Add checklist item to this task"
            onKeyPress={ignoreEnter(this.handleEnterKey)}
            className="checklist-item-description unstyled-textarea"
            value={newChecklistItem}
            onChange={event => this.setState({ newChecklistItem: event.target.value })}
            onBlur={() => this.addNewChecklistItem()}
            onPaste={this.pasteList}
            autoFocus={(addNewSubList || addItemInline) && !isMobile}
          />
        </div>
      </li>
    );
  }
}

class ChecklistItem extends React.Component {
  static propTypes = {
    task: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
    currentUser: PropTypes.object.isRequired,
    updateItem: PropTypes.func.isRequired,
    addItemInline: PropTypes.func.isRequired,
    item: PropTypes.object.isRequired
  };

  constructor() {
    super();

    this.state = {};

    this.completeChecklistItem = this.completeChecklistItem.bind(this);
    this.updateChecklistItem = this.updateChecklistItem.bind(this);
    this.removeChecklistItem = this.removeChecklistItem.bind(this);
    this.removeNewSubList = this.removeNewSubList.bind(this);
    this.handleEnterKey = this.handleEnterKey.bind(this);
  }

  completeChecklistItem(item) {
    const { currentUser, task } = this.props;
    const isActive = item.status === 'Active';

    // Don't allow changes to checklist items if the task is complete
    if (task.status === 'Complete') return;

    const updated = _.defaults({
      status: isActive ? 'Complete' : 'Active',
      completedBy: isActive ? currentUser.id : undefined,
      completedAt: isActive ? moment().toDate() : undefined
    }, item);
    
    this.props.updateItem(updated);
  }

  updateChecklistItem(item) {
    const { editChecklistItem } = this.state;

    const updated = _.defaults({ description: editChecklistItem }, item);

    this.props.updateItem(updated);
    this.setState({ focusedItem: null });
  }

  removeChecklistItem(item) {
    const updated = _.defaults({ status: 'Archived' }, item);

    this.props.updateItem(updated);
  }

  removeNewSubList() {
    this.setState({ addNewSubList: false });
  }

  handleEnterKey() {
    this.props.addItemInline(this.props.index + 1);
  }

  render() {
    const { item, index, task } = this.props;
    const { editChecklistItem, focusedItem, addNewSubList } = this.state;
    const complete = task.status === 'Complete';
    const subItems = _(task.checklist)
      .filter({ parent: item.id })
      .reject({ status: 'Archived' })
      .value();
    const showSubItems = !!subItems.length || addNewSubList;

    return (
      <li className="checklist-item">
        <div className={classNames('checklist-item-wrapper', `status-${item.status.toLowerCase()}`, { focused: focusedItem === index })}>
          <SVG onClick={() => this.completeChecklistItem(item)} className="task-checkbox" src="svg/check.svg" />
          { item.status === 'Complete' &&
            <span className="completed-by">(<UserInitials userId={item.completedBy} /> - <RelativeTime date={item.completedAt} />)</span>
          }
          { complete && <div className="checklist-item-description">{item.description}</div> }
          { !complete &&
            <Textarea
              className="checklist-item-description unstyled-textarea"
              value={_.get(item, 'id', 0) === focusedItem ? editChecklistItem : item.description}
              onKeyPress={ignoreEnter(this.handleEnterKey)}
              onChange={event => this.setState({ editChecklistItem: event.target.value })}
              onFocus={() => this.setState({ focusedItem: item.id, editChecklistItem: item.description })}
              onBlur={() => this.updateChecklistItem(item)}
            />
          }
          { (!complete && !showSubItems) && <SVG onClick={() => this.setState({ addNewSubList: true })} className="checklist-icon checklist-hidden-icon hoverable" src="svg/checklist.svg" /> }
          { (!complete) && <SVG onClick={() => this.removeChecklistItem(item)} className="archive checklist-hidden-icon hoverable" src="svg/delete.svg" /> }
        </div>
        { showSubItems &&
          <Checklist
            {...this.props}
            addNewSubList={addNewSubList}
            removeNewSubList={this.removeNewSubList}
            parent={item}
            isSublist
          />
        }
      </li>
    );
  }
}



// WEBPACK FOOTER //
// ./app/components/models/ticket/task-checklist.js